import type { ChipOwnProps, ChipProps } from '@mui/material';
import { Chip } from '@mui/material';
import type { InvoiceBillingStatus } from '@weft/types';
import { INVOICE_BILLING_STATUSES } from '@weft/types';
import { raiseUnreachable } from '@weft/utils';
import React from 'react';

type InvoiceBillingStatusLabelProps = {
  billingStatus: InvoiceBillingStatus;
} & Omit<ChipProps, 'label'>;

export const InvoiceBillingStatusLabel:React.FC<InvoiceBillingStatusLabelProps> = ({
  billingStatus,
  ...props
}) => {
  const { color, label } = ({
    [INVOICE_BILLING_STATUSES.None]: {
      color: 'primary',
      label: 'Open',
    },
    [INVOICE_BILLING_STATUSES.Paid]: {
      color: 'success',
      label: 'Paid',
    },
    [INVOICE_BILLING_STATUSES.Rejected]: {
      color: 'error',
      label: 'Cancelled',
    },
    [INVOICE_BILLING_STATUSES.Overdue]: {
      color: 'error',
      label: 'Overdue',
    },
    [INVOICE_BILLING_STATUSES.PendingPayment]: {
      color: 'secondary',
      label: 'Pending Payment',
    },
    [INVOICE_BILLING_STATUSES.OnHold]: {
      color: 'warning',
      label: 'On Hold',
    },
  } satisfies Record<InvoiceBillingStatus, {
    color: ChipOwnProps['color'],
    label: React.ReactNode;
  }>)[billingStatus];

  return (
    <Chip
      label={label}
      variant="outlined"
      color={color}
      {...props}
    />
  );
};

export function getBillingStatusLabel(billingStatus: InvoiceBillingStatus) {
  switch (billingStatus) {
    case INVOICE_BILLING_STATUSES.None:
      return 'Open';
    case INVOICE_BILLING_STATUSES.Paid:
      return 'Paid';
    case INVOICE_BILLING_STATUSES.Rejected:
      return 'Cancelled';
    case INVOICE_BILLING_STATUSES.PendingPayment:
      return 'Pending Payment';
    case INVOICE_BILLING_STATUSES.OnHold:
      return 'On Hold';
    case INVOICE_BILLING_STATUSES.Overdue:
      return 'Overdue';
    default:
      return raiseUnreachable(billingStatus);
  }
}
